/**
 * Product Extras - 加購商品、贈品、優惠組合
 * 處理變體同步、庫存檢查、多商品加入購物車
 */

(function () {
  'use strict';

  // ========== 工具函數 ==========

  /**
   * 從 /products/{handle}.json 取得商品資料
   */
  async function fetchProductJSON(handle) {
    try {
      const res = await fetch(`/products/${handle}.json`);
      if (!res.ok) return null;
      const data = await res.json();
      return data.product;
    } catch (e) {
      console.error('Failed to fetch product:', handle, e);
      return null;
    }
  }

  /**
   * 根據選擇的 options 找到對應的 variant
   * @param {Array} variants - 商品的所有 variants
   * @param {Object} selectedOptions - { option1: value, option2: value, ... }
   * @returns {Object|null} 找到的 variant
   */
  function findVariant(variants, selectedOptions) {
    return (
      variants.find(function (v) {
        return Object.keys(selectedOptions).every(function (key) {
          return v[key] === selectedOptions[key];
        });
      }) || null
    );
  }

  /**
   * 格式化價格（TWD 以整數顯示）
   */
  function formatPrice(priceStr, currency) {
    var price = parseFloat(priceStr) / 100;
    if (currency === 'TWD') {
      return 'NT$ ' + Math.round(price).toLocaleString('en-US');
    }
    return '$ ' + price.toLocaleString('en-US') + ' ' + currency;
  }

  /**
   * 機型字串正規化：轉小寫 + 去掉所有空白
   *
   * 後台各商品的「適用機型」選項值格式不一致，例如同一支機型在不同商品上是
   * 'iPhone 17 ProMax' / 'iPhone17 Pro Max'、'iPhone 16 Pro' / 'iPhone 16Pro'。
   * 精確比對會靜默失敗（找不到就什麼都不做，贈品停在錯的機型），
   * 所以比對前一律先正規化。
   */
  function normalizeOptionValue(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  /**
   * 解析「限定機型」中繼欄位
   *
   * Liquid 端用 join:'|' 輸出：清單型欄位會變成 a|b|c，
   * 多行文字欄位則原樣保留換行。這裡三種分隔符都吃，
   * 順便正規化，之後比對不用再處理格式差異。
   *
   * @returns {Array<string>} 正規化後的機型清單，空陣列代表不限制
   */
  function parseAllowedModels(raw) {
    if (!raw) return [];
    return String(raw)
      .split(/[|\n,]+/)
      .map(normalizeOptionValue)
      .filter(Boolean);
  }

  /**
   * 取得頁面幣別
   */
  function getCurrency() {
    return window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
      ? window.Shopify.currency.active
      : 'TWD';
  }

  // ========== ExtraItem 類別 ==========

  /**
   * 管理單一加購/贈品/優惠組合項目
   * @param {HTMLElement} el - 項目的根元素 (.product-extra-item)
   */
  function ExtraItem(el) {
    this.el = el;
    this.type = el.dataset.extraType; // 'addon', 'gift', 'bundledeal'
    this.handle = el.dataset.productHandle;
    this.discount = parseFloat(el.dataset.discount || '1');
    this.productData = null;
    this.currentVariant = null;
    this.isChecked = this.type === 'gift'; // 贈品預設勾選
    this.currency = getCurrency();
    // 主商品目前選的機型，這個項目能不能出現。
    // 不能出現時會隱藏，也不會被加入購物車。
    this.modelAvailable = true;
    // 限定機型白名單（來自中繼欄位 _gifts_models / _bundle_deal_models）。
    // 空陣列 = 不限制，只看贈品商品有沒有該機型的變體。
    this.allowedModels = parseAllowedModels(el.dataset.allowedModels);
    // 每個主商品選項各自的判斷結果（選項名 → 通過與否）。
    // 主商品可能有機型、顏色、款式多個選項，要全部通過才顯示，
    // 不能讓後處理的選項覆蓋掉前一個的結果。
    this._optionState = {};
    // 白名單有設定時，是否找到對應的「機型」選項維度
    this._modelDimensionFound = false;

    // 讀取內嵌的 variants JSON
    var jsonEl = el.querySelector('.extra-variants-json');
    if (jsonEl) {
      try {
        this.variants = JSON.parse(jsonEl.textContent);
      } catch (e) {
        this.variants = [];
      }
    } else {
      this.variants = [];
    }

    this._init();
  }

  ExtraItem.prototype._init = function () {
    var self = this;

    // 綁定 checkbox
    var checkbox = this.el.querySelector('.extra-checkbox');
    if (checkbox) {
      this.isChecked = checkbox.checked;
      checkbox.addEventListener('change', function () {
        self.isChecked = checkbox.checked;
        self._updateUI();
        self._dispatchChange();
      });
    }

    // bundledeal 的 toggle checkbox 在 .product-extra-item 外部，需額外綁定
    if (this.type === 'bundledeal') {
      var bundledealContainer = this.el.closest('.product-bundledeal');
      var toggleCheckbox = bundledealContainer && bundledealContainer.querySelector('.bundledeal-toggle');
      if (toggleCheckbox) {
        this.isChecked = toggleCheckbox.checked;
        toggleCheckbox.addEventListener('change', function () {
          self.isChecked = toggleCheckbox.checked;
          self._dispatchChange();
        });
      }
    }

    // 綁定變體選擇器
    var selects = this.el.querySelectorAll('.extra-variant-select');
    selects.forEach(function (select) {
      select.addEventListener('change', function () {
        self._onVariantChange();
      });
    });

    // 初始化：找到預設 variant
    this._onVariantChange();
  };

  /**
   * 當變體選擇變更時
   */
  ExtraItem.prototype._onVariantChange = function () {
    var selectedOptions = {};
    var selects = this.el.querySelectorAll('.extra-variant-select');
    selects.forEach(function (select) {
      selectedOptions['option' + select.dataset.optionIndex] = select.value;
    });

    // 如果沒有選擇器（單一變體），用第一個 variant
    if (selects.length === 0 && this.variants.length > 0) {
      this.currentVariant = this.variants[0];
    } else {
      this.currentVariant = findVariant(this.variants, selectedOptions);
    }

    this._updateUI();
    this._dispatchChange();
  };

  /**
   * 更新 UI 顯示（圖片、價格、庫存狀態）
   */
  ExtraItem.prototype._updateUI = function () {
    if (!this.currentVariant) return;

    var variant = this.currentVariant;

    // 更新圖片
    var imgEl = this.el.querySelector('.extra-cover img');
    if (imgEl && variant.featured_image) {
      imgEl.src = variant.featured_image.src;
      imgEl.alt = variant.featured_image.alt || '';
    }

    // 更新價格
    var priceEl = this.el.querySelector('.extra-price');
    if (priceEl) {
      if (this.type === 'gift') {
        // 贈品顯示原價 + Free
        priceEl.innerHTML =
          '<span class="extra-price-origin">' +
          formatPrice(variant.price, this.currency) +
          '</span>' +
          '<span class="extra-price-free">Free</span>';
      } else if (this.type === 'addon' && this.discount < 1) {
        // 加購有折扣
        var discountedPrice = Math.round(
          parseFloat(variant.price) * this.discount
        );
        priceEl.innerHTML =
          '<span class="extra-price-origin has-discount">' +
          formatPrice(variant.price, this.currency) +
          '</span>' +
          '<span class="extra-price-sale">' +
          formatPrice(discountedPrice.toString(), this.currency) +
          '</span>';
      } else if (this.type !== 'bundledeal') {
        // 一般價格
        priceEl.innerHTML = formatPrice(variant.price, this.currency);
      }
    }

    // 更新庫存狀態
    var stockEl = this.el.querySelector('.extra-stock-status');
    var checkbox = this.el.querySelector('.extra-checkbox');
    var isSoldOut = !variant.available;

    if (stockEl) {
      if (isSoldOut) {
        stockEl.textContent =
          this.type === 'gift' ? '贈品已贈送完畢' : '商品無庫存';
        stockEl.classList.add('sold-out');
        stockEl.classList.remove('hidden');
      } else {
        stockEl.classList.add('hidden');
        stockEl.classList.remove('sold-out');
      }
    }

    // 售完時停用 checkbox
    if (checkbox && isSoldOut) {
      checkbox.checked = false;
      checkbox.disabled = true;
      this.isChecked = false;
    } else if (checkbox && !isSoldOut) {
      checkbox.disabled = false;
    }

    // BundleDeal 售完提示
    if (this.type === 'bundledeal') {
      var errorEl = this.el.querySelector('.extra-error');
      if (errorEl) {
        if (isSoldOut) {
          errorEl.textContent = '商品已售完，請選擇其它商品';
          errorEl.classList.remove('hidden');
        } else {
          errorEl.classList.add('hidden');
        }
      }
    }
  };

  /**
   * 這個項目自己的外框（隱藏單一項目時的對象）
   *
   * 加購區塊是迴圈渲染的，一個 .product-addons 裡會有多個項目，
   * 所以隱藏必須以項目為單位，不能直接藏整塊。
   */
  ExtraItem.prototype._getItemWrap = function () {
    return this.el.closest('li') || this.el;
  };

  /**
   * 這個項目所屬的區塊（贈品／優惠組合／加購）
   */
  ExtraItem.prototype._getBlock = function () {
    return this.el.closest(
      '.product-gifts, .product-bundledeal, .product-addons'
    );
  };

  /**
   * 區塊裡的項目全被隱藏時，連同區塊標題一起藏起來，
   * 避免留下一個只有標題、內容空白的區塊。
   */
  ExtraItem.prototype._refreshBlockVisibility = function () {
    var block = this._getBlock();
    if (!block) return;

    var items = block.querySelectorAll('.product-extra-item');
    var anyVisible = Array.prototype.some.call(items, function (item) {
      var wrap = item.closest('li') || item;
      return !wrap.classList.contains('hidden');
    });

    block.classList.toggle('hidden', !anyVisible);
  };

  /**
   * 把跟著主商品連動的選擇器鎖住，改用純文字顯示。
   *
   * 這個選項的值必須等於主商品的機型，讓使用者能改只會選出機型不符的組合，
   * 所以直接不給改。
   */
  ExtraItem.prototype._lockSelect = function (select, label) {
    var wrap = select.closest('.select') || select;
    wrap.classList.add('hidden');
    select.dataset.lockedByMain = 'true';

    var lockedEl = this.el.querySelector('.extra-locked-option');
    if (!lockedEl) {
      lockedEl = document.createElement('span');
      lockedEl.classList.add('extra-locked-option');
      wrap.parentNode.insertBefore(lockedEl, wrap);
    }
    lockedEl.textContent = label;
  };

  /**
   * 設定「主商品目前的機型，這個項目有沒有對應變體」
   * 沒有的話整塊隱藏，並取消勾選，避免被加入購物車。
   */
  ExtraItem.prototype._setModelAvailable = function (available) {
    if (this.modelAvailable === available) return;
    this.modelAvailable = available;

    var block = this._getBlock();
    this._getItemWrap().classList.toggle('hidden', !available);
    this._refreshBlockVisibility();

    if (!available) {
      // 隱藏時一併取消勾選，包含 bundledeal 在項目外層的那顆 toggle
      this.isChecked = false;
      var boxes = [this.el.querySelector('.extra-checkbox')];
      if (block) boxes.push(block.querySelector('.bundledeal-toggle'));
      boxes.forEach(function (box) {
        if (box) box.checked = false;
      });
      var content = block && block.querySelector('.bundledeal-content');
      if (content) content.classList.remove('is-visible');
    } else if (this.type === 'gift') {
      // 贈品重新出現時恢復預設勾選（售完的話 _updateUI 會把 checkbox 停用）
      this.isChecked = true;
      var giftBox = this.el.querySelector('.extra-checkbox');
      if (giftBox && !giftBox.disabled) giftBox.checked = true;
    }

    this._dispatchChange();
  };

  /**
   * 綜合所有主商品選項的判斷結果，決定這個項目要不要出現
   */
  ExtraItem.prototype._recomputeAvailability = function () {
    var state = this._optionState;
    var ok = Object.keys(state).every(function (key) {
      return state[key];
    });
    this._setModelAvailable(ok);
  };

  /**
   * 套用主商品的選項值（機型連動的入口）
   *
   * 主商品可能有多個選項（適用機型、顏色、款式…），每個都會呼叫一次。
   * 各自的結果存在 _optionState，全部通過才顯示。
   *
   * @param {string} optionName - 主商品的選項名稱，例如「適用機型」
   * @param {string} optionValue - 目前選的值，例如「iPhone 18 Pro」
   * @param {Array<string>} optionValues - 該選項的所有可選值，用來判斷這是不是「機型」維度
   */
  ExtraItem.prototype.applyMainOption = function (
    optionName,
    optionValue,
    optionValues
  ) {
    var self = this;
    var key = normalizeOptionValue(optionName);
    var value = normalizeOptionValue(optionValue);

    // 第二層：白名單。
    //
    // 白名單講的是「機型」，但主商品還有顏色、款式等其他選項，
    // 那些值當然不會在機型白名單裡 —— 直接比對會把贈品誤判成不該顯示。
    // 所以先用「這個選項的可選值集合有沒有跟白名單交集」判斷它是不是機型維度，
    // 只有機型維度才套用白名單。
    if (this.allowedModels.length > 0) {
      var values = (optionValues || []).map(normalizeOptionValue);
      var isModelDimension = values.some(function (v) {
        return self.allowedModels.indexOf(v) !== -1;
      });

      if (isModelDimension) {
        this._modelDimensionFound = true;
        if (this.allowedModels.indexOf(value) === -1) {
          this._optionState[key] = false;
          this._recomputeAvailability();
          return;
        }
      }
    }

    var target = null;
    this.el.querySelectorAll('.extra-variant-select').forEach(function (select) {
      if (normalizeOptionValue(select.dataset.optionName) === key) {
        target = select;
      }
    });

    // 這個項目沒有同名選項（例如贈品只有單一顏色，Liquid 不會渲染選擇器）
    // → 這個維度不構成限制
    if (!target) {
      this._optionState[key] = true;
      this._recomputeAvailability();
      return;
    }

    // 選項名稱相同，不代表講的是同一件事。
    //
    // 例：主商品 aurora-v3 的「顏色」是殼的透明度（值只有「透明」），
    // 贈品 handy 的「顏色」是背繩顏色（米蘭棕、冰川藍…）。兩邊毫無交集，
    // 硬要比對會讓贈品永遠找不到對應值而被隱藏。
    //
    // 所以先看兩邊的可選值有沒有交集：沒有交集就是不同維度，
    // 既不連動也不過濾，讓使用者自己選（背繩顏色本來就該讓客人挑）。
    var targetValues = Array.prototype.map.call(target.options, function (opt) {
      return normalizeOptionValue(opt.value);
    });
    var mainValues = (optionValues || []).map(normalizeOptionValue);
    var sharesDomain = mainValues.some(function (v) {
      return targetValues.indexOf(v) !== -1;
    });

    if (!sharesDomain) {
      this._optionState[key] = true;
      this._recomputeAvailability();
      return;
    }

    // 第一層：贈品商品沒有該值對應的變體 → 不出現
    var match = Array.prototype.slice.call(target.options).find(function (opt) {
      return normalizeOptionValue(opt.value) === value;
    });

    if (!match) {
      this._optionState[key] = false;
      this._recomputeAvailability();
      return;
    }

    target.value = match.value;
    this._lockSelect(target, match.textContent.trim());
    this._optionState[key] = true;
    this._recomputeAvailability();
    this._onVariantChange();
  };

  /**
   * 白名單有設定卻對不上任何選項維度時提醒一次。
   * 通常是中繼欄位裡的機型名稱跟商品選項值差太多（不只是空白或大小寫差異）。
   */
  ExtraItem.prototype.warnIfWhitelistUnmatched = function () {
    if (this.allowedModels.length === 0 || this._modelDimensionFound) return;
    console.warn(
      '[ProductExtras] 限定機型的中繼欄位對不上任何商品選項，此限制不會生效。' +
        ' 商品：' + this.handle +
        '，白名單：' + this.allowedModels.join(', ')
    );
  };

  /**
   * 取得要加入購物車的資料
   * @returns {Object|null} { id, quantity, properties }
   */
  ExtraItem.prototype.getCartLine = function () {
    if (
      !this.isChecked ||
      !this.modelAvailable ||
      !this.currentVariant ||
      !this.currentVariant.available
    ) {
      return null;
    }

    return {
      id: this.currentVariant.id,
      quantity: 1,
    };
  };

  /**
   * 發送自訂事件通知外部狀態改變
   */
  ExtraItem.prototype._dispatchChange = function () {
    this.el.dispatchEvent(
      new CustomEvent('extra:change', {
        bubbles: true,
        detail: {
          type: this.type,
          variant: this.currentVariant,
          isChecked: this.isChecked,
        },
      })
    );
  };

  // ========== ProductExtrasManager ==========

  /**
   * 管理所有加購/贈品/優惠組合，攔截表單提交
   */
  function ProductExtrasManager(sectionId) {
    this.sectionId = sectionId;
    this.items = [];
    this.form = null;

    this._init();
  }

  ProductExtrasManager.prototype._init = function () {
    var self = this;
    var container = document.getElementById('MainProduct-' + this.sectionId);
    if (!container) return;

    // 初始化所有 extra items
    var extraEls = container.querySelectorAll('.product-extra-item');
    extraEls.forEach(function (el) {
      self.items.push(new ExtraItem(el));
    });

    if (this.items.length === 0) return;

    // 監聽主商品變體變更
    var variantSelects = container.querySelector('variant-selects');
    if (variantSelects) {
      var mainSelects = variantSelects.querySelectorAll('select');

      mainSelects.forEach(function (select) {
        select.addEventListener('change', function () {
          self._syncFromMainSelect(select);
        });
      });

      // 載入時先套用一次。
      // 贈品的選擇器預設是自己的第一個選項，不主動同步的話一進頁面就可能
      // 停在跟主商品不同的機型。
      mainSelects.forEach(function (select) {
        self._syncFromMainSelect(select);
      });

      // 白名單設了卻對不上任何選項維度時提醒，避免限制無聲失效
      this.items.forEach(function (item) {
        item.warnIfWhitelistUnmatched();
      });
    }

    // 攔截表單提交，改用多商品加入購物車
    // 使用 capture phase 在父容器上監聽，確保在 product-form.js 的 bubble 監聽器之前攔截，
    // 避免主商品被重複加入購物車，並確保 cart-notification 由本模組統一處理
    this.form = container.querySelector('form[data-type="add-to-cart-form"]');
    if (this.form) {
      container.addEventListener('submit', function (e) {
        if (e.target !== self.form) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        self._handleSubmit();
      }, true); // capture phase
    }
  };

  /**
   * 從主商品的某個選擇器讀出選項名稱與值，套用到所有 extra items
   *
   * 選項名稱取自 name="options[適用機型]"，比抓 label 文字可靠
   * （label 可能被多語系或版型調整影響）。
   */
  ProductExtrasManager.prototype._syncFromMainSelect = function (select) {
    var matched = /options\[(.+)\]/.exec(select.name || '');
    var optionName = matched
      ? matched[1]
      : select
          .closest('.product-form__input')
          ?.querySelector('.form__label')
          ?.textContent?.trim();

    if (!optionName) return;

    // 一併傳這個選項的所有可選值，讓項目能判斷這是不是「機型」維度
    var optionValues = Array.prototype.map.call(
      select.options,
      function (opt) {
        return opt.value;
      }
    );

    this.items.forEach(function (item) {
      item.applyMainOption(optionName, select.value, optionValues);
    });
  };

  /**
   * 處理表單提交 - 收集所有商品並一次加入購物車
   */
  ProductExtrasManager.prototype._handleSubmit = async function () {
    var submitBtn = this.form.querySelector('[type="submit"]');
    var spinner = submitBtn?.querySelector('.loading-overlay__spinner');
    var btnText = submitBtn?.querySelector('span');

    // 顯示載入狀態
    if (spinner) spinner.classList.remove('hidden');
    if (btnText) btnText.classList.add('hidden');
    if (submitBtn) submitBtn.disabled = true;

    // 收集主商品
    var mainVariantId = this.form.querySelector('input[name="id"]')?.value;
    var items = [];

    if (mainVariantId) {
      items.push({
        id: parseInt(mainVariantId),
        quantity: 1,
      });
    }

    // 收集所有 extras
    this.items.forEach(function (item) {
      var line = item.getCartLine();
      if (line) {
        items.push(line);
      }
    });

    try {
      var cartNotification = document.querySelector('cart-notification');
      var body = { items: items };
      if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
        body.sections = cartNotification.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
      }

      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        var data = await res.json();

        // 顯示 cart-notification
        if (cartNotification && data.sections) {
          var firstItem = Array.isArray(data.items) ? data.items[0] : data;
          if (firstItem && firstItem.key) {
            try {
              cartNotification.renderContents({ key: firstItem.key, sections: data.sections });
            } catch (e) {
              console.warn('[ProductExtras] renderContents failed:', e);
            }
          }
        }

        // 更新購物車 UI
        await this._updateCartUI();
      } else {
        var errData = await res.json().catch(function () {
          return {};
        });
        console.error('Cart add error:', errData);
        var errorWrapper = this.form.querySelector(
          '.product-form__error-message-wrapper'
        );
        var errorMsg = this.form.querySelector('.product-form__error-message');
        if (errorWrapper && errorMsg) {
          errorMsg.textContent = errData.description || '加入購物車失敗';
          errorWrapper.removeAttribute('hidden');
        }
      }
    } catch (err) {
      console.error('Cart add failed:', err);
    } finally {
      // 恢復按鈕狀態
      if (spinner) spinner.classList.add('hidden');
      if (btnText) btnText.classList.remove('hidden');
      if (submitBtn) submitBtn.disabled = false;
    }
  };

  /**
   * 更新購物車計數和側邊欄
   */
  ProductExtrasManager.prototype._updateCartUI = async function () {
    try {
      var res = await fetch('/cart.js');
      var cartData = await res.json();

      // 更新購物車數量
      var countEls = document.querySelectorAll(
        '.js-cart-item-count, .cart-count-bubble span[aria-hidden]'
      );
      countEls.forEach(function (el) {
        el.textContent = cartData.item_count;
      });

    } catch (e) {
      console.error('Failed to update cart UI:', e);
    }
  };

  // ========== BundleDeal 展開/收合 ==========

  function initBundleDealToggle() {
    document
      .querySelectorAll('.bundledeal-toggle')
      .forEach(function (checkbox) {
        var container = checkbox.closest('.product-bundledeal');
        var content = container?.querySelector('.bundledeal-content');
        if (!content) return;

        checkbox.addEventListener('change', function () {
          if (checkbox.checked) {
            content.classList.add('is-visible');
          } else {
            content.classList.remove('is-visible');
          }
        });
      });
  }

  // ========== 初始化 ==========

  function init() {
    // 找到所有 product section
    document.querySelectorAll('[data-section]').forEach(function (section) {
      var sectionId = section.dataset.section;
      var hasExtras = section.querySelector('.product-extra-item');
      if (hasExtras) {
        new ProductExtrasManager(sectionId);
      }
    });

    initBundleDealToggle();
  }

  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
